"use client";

import { ArrowRight, Clock, CircleCheck as CheckCircle2, TrendingUp, ShieldCheck, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePerformanceModel() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const specialties = [{
    id: 1,
    label: "Qualified Lead Generation",
    description: "Pre-screened, high-intent prospective policyholders ready for conversion."
  }, {
    id: 2,
    label: "Appointment Setting",
    description: "Direct calendar placement with decision-makers seeking immediate coverage."
  }, {
    id: 3,
    label: "Customer Outreach",
    description: "Systematic multi-channel contact cadence executing targeted touchpoints."
  }, {
    id: 4,
    label: "Sales Support Systems",
    description: "End-to-end qualification and administrative workflows supporting your pipeline."
  }];
  const modelPillars = [{
    icon: ShieldCheck,
    title: "Zero Upfront Capital",
    description: "No monthly retainers or setup fees. We deploy our infrastructure upfront at zero risk to your agency."
  }, {
    icon: TrendingUp,
    title: "Aligned Performance",
    description: "Our compensation is tied directly to closed policies and sustained retention, ensuring shared incentives."
  }, {
    icon: Zap,
    title: "Ready-to-Submit Customers",
    description: "Eliminate cold prospecting with vetted, high-intent individuals requiring insurance solutions."
  }];
  return <section id="performance-model" className="py-24 md:py-32 px-6 lg:px-12 bg-slate-50/60 dark:bg-background border-b border-border/80">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Architectural Overview & Value Proposition */}
          <div ref={leftRef} className={`lg:col-span-5 flex flex-col justify-between transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="h-2 w-2 rounded-lg bg-accent" />
                <span className="font-mono text-xs tracking-widest text-accent font-bold uppercase">
                  § Performance Architecture
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-[1.12] mb-6">
                A Partnership Model Built on Closed Results
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
                Traditional lead brokers charge hefty retainers regardless of outcome. RMA Solutions operates on a true
                performance alignment: we deploy outbound marketing, vetting, and appointment setting with zero upfront
                costs, growing exclusively when your agency closes.
              </p>

              {/* Model Pillars */}
              <div className="space-y-4 mb-8">
                {modelPillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return <div key={pillar.title} data-index={i} className="p-4 sm:p-5 rounded-xl border border-border/80 bg-card shadow-xs transition-all duration-200 hover:shadow-md hover:border-accent/30 flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-accent/15 text-accent dark:bg-accent/20 shrink-0 mt-0.5">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-foreground tracking-tight mb-1">
                          {pillar.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* SLA & CTA */}
            <div className="pt-6 border-t border-border/70">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-6">
                <Clock className="size-4 text-accent shrink-0" />
                <span>Rapid deployment: qualified outreach initiated within 7 business days</span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm rounded-lg px-6 h-11 font-semibold text-sm transition-all duration-200">
                  <Link to="/GetStartedPage">
                    Get Started Now
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="border-border text-foreground hover:bg-secondary hover:text-foreground shadow-xs rounded-lg px-6 h-11 font-medium text-sm transition-all duration-200">
                  <Link to="/PartnerProgramPage">
                    View Partner Terms
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Execution Specialties Grid */}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="p-6 sm:p-8 rounded-xl bg-card border border-border/80 shadow-md">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 mb-6 border-b border-border/80">
                <div>
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                    Specialized Delivery
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                    Core Operational Pillars
                  </h3>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary border border-border/60 text-xs font-semibold text-foreground self-start sm:self-auto shadow-xs">
                  <CheckCircle2 className="size-3.5 text-accent" />
                  <span>100% Agency Focused</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                {specialties.map((specialty, i) => <div key={specialty.id} data-index={i} className="p-5 rounded-xl border border-border/80 bg-background/80 hover:bg-card hover:border-accent/40 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs font-bold text-accent bg-accent/10 px-2 py-0.5 rounded-md">
                          0{specialty.id}
                        </span>
                        <div className="size-2 rounded-lg bg-accent/50" />
                      </div>
                      <h4 className="text-base font-bold text-foreground tracking-tight mb-2">
                        {specialty.label}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {specialty.description}
                      </p>
                    </div>

                    <div className="pt-4 mt-4 border-t border-border/60 flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Status</span>
                      <span className="text-xs font-semibold text-foreground inline-flex items-center gap-1.5">
                        <span className="size-1.5 rounded-lg bg-accent" /> Active Scale
                      </span>
                    </div>
                  </div>)}
              </div>

              {/* Bottom Summary Bar */}
              <div className="mt-6 p-4 rounded-xl bg-secondary/80 border border-border/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                <div className="text-muted-foreground">
                  <span className="font-semibold text-foreground">Zero retainers. Zero upfront tech fees.</span> Real appointments booked straight to your CRM.
                </div>
                <Link to="/GrowthEnginePage" className="inline-flex items-center gap-1 text-primary dark:text-accent font-semibold hover:underline shrink-0">
                  Explore Engine <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}